<template>
	<NuxtLayout
		name="slideshow"
		:slides="homepageSlides"
		:background-video="{
			mp4Src: cdnUrl('fysio-hier-homepage-header.mp4'),
			webmSrc: cdnUrl('fysio-hier-homepage-header.webm'),
			posterImg: {
				src: '/img/building/sportruimte-fysio-hier.jpg',
				alt: 'Fysio Hier sportruimte',
				preload: true,
				width: 1920,
				height: 1080,
			},
		}"
	>
		<div
			v-if="config.public.flagDisplayReviews && aggregateScore.rating >= 7"
			class="flex items-center justify-center pt-28"
		>
			<LazyOrganismRating
				:rating="aggregateScore.rating"
				:total-reviews="aggregateScore.totalReviews"
				:review-link="aggregateScore.url"
			/>
		</div>
		<div class="flex flex-col gap-24 md:gap-56 bg-gray-50 pt-28 pb-56">
			<div class="container px-0 md:px-8 flex flex-col gap-24 md:gap-40">
				<div>
					<AtomTitle
						tag="h1"
						size="xl"
						class="items-center lg:items-start mb-16 md:mb-24 text-purple-900"
					>
						Fysiotherapie, waarom Hier?
					</AtomTitle>
					<OrganismCardGrid :cards="usps" />
				</div>
			</div>

			<OrganismInsurers
				class="container max-w-screen-lg"
				title="Fysiotherapie in jouw verzeking"
				description="Wij zijn aangesloten bij alle Nederlandse zorgverzekeraars. Met een aanvullende verzekering wordt jouw behandeling dus gedekt!"
				:insurers="insurers"
			/>

			<div class="container px-0 md:px-8 flex flex-col gap-24 text-center">
				<AtomTitle
					tag="h2"
					size="2xl"
				>
					Samenwerkingen waar wij trots op zijn.
				</AtomTitle>
				<div class="grid md:grid-cols-3 gap-12">
					<AtomCard class="p-12 flex flex-col gap-4">
						<AtomTitle
							tag="h3"
							size="lg"
						>
							Defensie
						</AtomTitle>
						<p class="text-paragraph-size-sm text-gray-700">
							Militairen vanuit de vliegbasis Volkel en de kazerne in Oirschot behandelen wij wanneer interne therapie ontoereikend of bezet is.
						</p>
						<p class="text-paragraph-size-sm text-gray-700">
							We dragen graag bij om de gedreven mannen en vrouwen van defensie fysiek fit te houden. Wij zijn aangesloten bij het SZVK.
						</p>
						<div class="flex justify-center">
							<AtomImage
								src="/img/health-insurers/szvk.png"
								alt="Stichting Ziektekostenverzekering Krijgsmacht logo"
								:width="123"
								:height="79"
							/>
						</div>
					</AtomCard>
					<AtomCard class="p-12 flex flex-col gap-4">
						<AtomTitle
							tag="h3"
							size="lg"
						>
							TeamNL voltige team
						</AtomTitle>
						<p class="text-paragraph-size-sm text-gray-700">
							We trainen het internationale team van de Voltige Vereniging in Odiliapeel en zijn trotse sponsor van TeamNL voltigeur Niek Blokland.
						</p>
						<p class="text-paragraph-size-sm text-gray-700">
							Zo helpen wij hen een stukje verder op weg naar internationale topprestaties en richting de Olympische Spelen van 2032.
						</p>
						<div class="flex justify-center pt-12">
							<AtomImage
								src="/img/brands/teamnl.png"
								alt="TeamNL logo"
								:width="181"
								:height="30"
							/>
						</div>
					</AtomCard>
					<AtomCard class="p-12 flex flex-col gap-4">
						<AtomTitle
							tag="h3"
							size="lg"
						>
							Lokale sportverenigingen
						</AtomTitle>
						<p class="text-paragraph-size-sm text-gray-700">
							Activiteit en sportiviteit is ontzettend belangrijk voor een gezonde en sociale samenleving. Wij zijn sponsor van De Keien Atletiek in Uden.
						</p>
						<p class="text-paragraph-size-sm text-gray-700">
							Op deze manier houden we samen sport en beweging toegankelijk en dragen we bij aan een gezond verrenigingsleven in Uden!
						</p>
						<div class="flex justify-center">
							<AtomImage
								src="/img/brands/de-keien-atletiek.svg"
								alt="De Keien Atletiek logo"
								:width="150"
								:height="85"
							/>
						</div>
					</AtomCard>
				</div>
			</div>

			<MoleculeBackgroundSection width="md">
				<AtomTitle
					tag="h2"
					size="2xl"
					class="items-center lg:items-start mb-24"
					underline
				>
					Fysiotherapie met kennis over jouw klacht
				</AtomTitle>
				<div class="flex flex-col gap-20 md:gap-32 pb-20 mb-12">
					<MoleculeContentBlockImage
						v-for="({ title, text, injury }, index) in featuredInjuries"
						:key="`homepage-featured-injury-${index}-${injury.title}`"
						:title="title"
						:text="text"
						:link="{
							text: `Meer over ${injury.title}`,
							to: injury.pageUrl as UrlType,
						}"
						:image="{
							alt: injury.title,
							src: injury.illustration,
						}"
						:image-position="index % 2 ? 'right' : 'left'"
					/>
				</div>
				<div class="flex justify-center">
					<AtomButton
						text="Maak een afspraak"
						icon-left="fa-calendar-days"
						:variant="ButtonVariant.Yellow"
						size="lg"
						@click="
							() => {
								trackButtonClick('HomepageInjuries_CTA_FysioAppointment');
								openAppointmentDialog();
							}
						"
					/>
				</div>
			</MoleculeBackgroundSection>
			<div
				v-if="config.public.flagDisplayReviews"
				class="lg:container"
			>
				<LazyAtomTitle
					tag="h2"
					size="xl"
					underline
					class="text-purple-900 items-center lg:items-start mb-24 text-center lg:text-left"
				>
					Wat patiënten vertellen over Fysio Hier
				</LazyAtomTitle>
				<LazyOrganismReviews
					:reviews="reviews"
					:rating="aggregateScore.rating"
					:total-reviews="aggregateScore.totalReviews"
					:review-link="aggregateScore.url"
				/>
			</div>

			<div class="container flex flex-col gap-12">
				<AtomTitle
					tag="h2"
					size="lg"
				>
					Laatste blog items
				</AtomTitle>
				<OrganismBlogItems
					:filter="false"
					:limit="3"
					:pagination="false"
				/>
				<div class="flex justify-center">
					<AtomButton
						:to="{ url: '/blog', title: 'Fysio Hier Blog' }"
						text="Bekijk alle berichten"
						:variant="ButtonVariant.DarkBorder"
					/>
				</div>
			</div>

			<div class="container flex flex-col gap-16">
				<div class="lg:max-w-7xl">
					<AtomTitle
						tag="h2"
						size="xl"
						underline
						class="text-purple-900 items-center lg:items-start mb-10 text-center lg:text-left"
					>
						Voorkom blessures, kom met ons trainen!
					</AtomTitle>

					<div
						class="flex flex-col gap-12 text-gray-900 text-paragraph-size-md text-center lg:text-left"
					>
						<p>
							Kom trainen in kleine groepen van maximaal 6 personen met professionele trainers. Liever met personal
							training werken aan je persoonlijke doelen? Dat kan ook! 1-op-1 of in duo's. Of kom met jouw eigen groep
							trainen in Private Group Training!
						</p>

						<div class="mb-12 flex justify-center lg:justify-start">
							<AtomButton
								text="Start trainen bij Fit Hier"
								size="lg"
								icon-right="fa-arrow-right"
								:to="{
									url: config.public.fitHierUrl,
									title: 'Fit Hier lidmaatschappen bekijken',
									newTab: true,
								}"
								@click="trackButtonClick('HomepageBottom_CTA_ViewFitnessSubscriptions')"
							/>
						</div>
					</div>
				</div>

				<OrganismBanner
					title="Trainen met de persoonlijke begeleiding"
					subtitle="Doe gratis en vrijblijvend mee aan een les Small Group Training en ervaar hoe leuk en motiverend sporten onder begeleiding van een trainer is!"
					text="test"
					button-text="Gratis uitproberen"
					:to="{
						url: `${config.public.fitHierUrl}/small-group-training`,
						title: 'Aanmelden voor een gratis proefles bij Fit Hier',
						newTab: true,
					}"
				/>
			</div>
		</div>
	</NuxtLayout>
</template>

<script lang="ts" setup>
import insurers from '#base/core/consts/healthInsurers';
import homepageSlides from '#fysio/core/consts/homepageSlides';
import usps from '#base/core/consts/usps';
import { ButtonVariant } from '#base/core/enums/ButtonVariantEnum';
import { cdnUrl } from '#base/helpers/contentHelpers';
import { getFeaturedInjuries } from '#fysio/helpers/contentHelpers';
import { getAggregateScore, getReviews } from '#base/helpers/reviewHelpers';
import { formatSchema } from '#base/helpers/schemaHelpers';
import { medicalBusinessSchema } from '#fysio/helpers/schemaHelpers';
import type { UrlType } from '#base/core/types/UrlType';

const config = useRuntimeConfig();
const { trackButtonClick } = useEventTracking();

useSeoMeta({
	title: 'Fysiotherapie Uden – Persoonlijke Zorg | Fysio Hier',
	ogTitle: 'Fysiotherapie met persoonlijke zorg, Fysio Hier in Uden',
	description:
        'Persoonlijke fysiotherapie en revalidatie in Uden. Snelle behandeling, contracten met alle zorgverzekeraars. Plan je afspraak eenvoudig online.',
});

useHead({
	script: [formatSchema(medicalBusinessSchema)],
});

const { openAppointmentDialog } = useDialogs();

const featuredInjuries = getFeaturedInjuries();
const aggregateScore = getAggregateScore();
const reviews = getReviews();
</script>
